import React, { memo, useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { InsuranceBuyingStepRoute } from 'common/enums/app-route-path.enum';

function getTabNumberColor(isActive: boolean, isCompleted: boolean): string {
  if (isCompleted) {
    return 'primary.main';
  }

  if (isActive) {
    return 'grey.50';
  }

  return 'grey.400';
}

export interface IStepperItem<T extends string> {
  title: string;
  path: T;
}

interface StepperProps<T extends string> {
  data: IStepperItem<T>[];
  onNavigate?: (path: string, index: number) => void;
}

const styles = {
  root: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  tabs: {
    display: 'flex',
  },
  progressLine: {
    mt: 1.2,
    width: '100%',
    height: '3px',
    borderRadius: 1,
    bgcolor: 'grey.300',
  },
  innerProgressLine: (filledWidth: number) => ({
    width: `${filledWidth}%`,
    height: '100%',
    borderRadius: 1,
    transition: 'all 0.5s ease-out',
    background:
      'linear-gradient(90deg, #0049C3 0.04%, #4994F0 99.96%); linear-gradient(90deg, color(display-p3 0.004 0.282 0.737) 0.04%, color(display-p3 0.361 0.573 0.914) 99.96%)',
  }),
  tab: (isCompletedOrEquivalent: boolean) => ({
    display: 'flex',
    flexDirection: {
      xs: 'column',
      sm: 'row',
    },
    alignItems: 'center',
    gap: 1,
    color: isCompletedOrEquivalent ? 'primary.main' : 'grey.400',
    transition: 'all 0.3s ease-out',
    '&:hover': {
      cursor: isCompletedOrEquivalent ? 'pointer' : '',
    },
  }),
  tabNumber: (isActive: boolean, isCompleted: boolean) => ({
    width: 28,
    minWidth: 28,
    height: 28,
    borderRadius: '50%',
    bgcolor: isActive ? 'primary.main' : 'grey.50',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: getTabNumberColor(isActive, isCompleted),
    boxShadow: isActive ? '0px 0px 4px 0px #0047BB' : 'none',
    transition: 'all 0.3s ease-out',
  }),
  title: {
    maxWidth: '100%',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
  },
};

function InsuranceBuyingStepper<T extends string>({
  data,
  onNavigate,
}: StepperProps<T>) {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(-1);
  const filledWidth = (100 / data.length) * (activeTab + 1);

  // set the active tab
  useEffect(() => {
    const activePath = location.pathname.split('/').filter(Boolean).pop();

    setActiveTab(data.findIndex((tab) => tab.path === activePath));
  }, [data, location.pathname]);

  const handleTabClick = useCallback(
    (index: number, path: T) => {
      if (
        index > activeTab ||
        location.pathname.includes(InsuranceBuyingStepRoute.PAYMENT_INFO)
      )
        return () => {};

      return () => {
        setActiveTab(index);

        if (onNavigate) {
          onNavigate(path, index);

          return;
        }

        navigate(path, {
          state: location.state,
        });
      };
    },
    [activeTab, location, onNavigate, navigate]
  );

  if (activeTab < 0) return null;

  return (
    <Box sx={styles.root}>
      <Box sx={styles.tabs}>
        {data.map((step, index) => (
          <Box
            key={step.title}
            sx={styles.tab(activeTab >= index)}
            width={`calc(100% / ${data.length})`}
            onClick={handleTabClick(index, step.path)}
          >
            <Box
              sx={{
                ...styles.tabNumber(activeTab === index, activeTab > index),
              }}
            >
              <Typography fontWeight="bold" variant="caption">
                {index + 1}
              </Typography>
            </Box>
            <Typography variant="body2" sx={styles.title}>
              {t(step.title)}
            </Typography>
          </Box>
        ))}
      </Box>
      <Box sx={styles.progressLine}>
        <Box sx={styles.innerProgressLine(filledWidth)} />
      </Box>
    </Box>
  );
}

export default memo(InsuranceBuyingStepper) as typeof InsuranceBuyingStepper;
